import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { MODALS_ID, QUERY_KEY } from '../../constants'

import { toast } from '../../utils'

import { getUserProductDetail, getUserProductSettle } from '../../api/product'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useNavigate } from '../../hooks/useNavigate'
import useParamId from '../../hooks/useParamId'
import { useUserInfo } from '../../hooks/useUserInfo'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, CenterWrap, ColumnItem, RowBetween } from '../../components/common'
import { Button } from '../../components/button'
import { Card } from '../../components/card/Card'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Input } from '../../components/input'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Footer, FOOTER_MIN_HEIGHT } from '../../components/footer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { RowData } from './components'
import { CalculateConfirmModal } from './modals/CalculateConfirmModal'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowRightGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray_18.svg'
import { ReactComponent as ArrowDownRoundIcon } from '../../assets/ico/common/expand/expand_roundDown.svg'
import { ReactComponent as ArrowDownIcon } from '../../assets/ico/common/down_18.svg'
import { ReactComponent as CheckedIcon } from '../../assets/ico/common/check/check_checked_blue_fill.svg'
import { ReactComponent as UncheckedIcon } from '../../assets/ico/common/check/check_default_gray_fill.svg'

interface Accordion {
  sale: boolean
  amount: boolean
}

interface Agreed {
  procedure: boolean
  personalProvide: boolean
  duty: boolean
}

interface Rrn {
  value: string
  masking: string
}

const QUERY_OPTIONS = {
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
}

const RRN_REGEX = /^[0-9\-\●]*$/

export function ProductCalculate() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)

  const [accordion, setAccordion] = useState<Accordion>({ sale: false, amount: true })
  const [agreed, setAgreed] = useState<Agreed>({
    procedure: false,
    personalProvide: false,
    duty: false,
  })
  const [rrn, setRrn] = useState<Rrn>({ value: '', masking: '' })

  const userProductId = useParamId(3)
  const { data: userInfo, isLogin, isCheckingLogin } = useUserInfo()
  const navigate = useNavigate()

  // 로그인 유저 보유중인 작품 상세
  const userProductDetail = useQuery({
    queryKey: [QUERY_KEY.userProduct, { userProductId }],
    queryFn: async () => await getUserProductDetail(userProductId as number),
    enabled: typeof userProductId === 'number',
    ...QUERY_OPTIONS,
  })
  // 로그인 유저 보유중인 작품 정산 정보
  const userProductSettle = useQuery({
    queryKey: [QUERY_KEY.userProductSettle, { userProductId }],
    queryFn: async () => await getUserProductSettle(userProductId as number),
    enabled: typeof userProductId === 'number',
    ...QUERY_OPTIONS,
  })

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (userProductDetail.data?.status !== 'TO_BE_SETTLEMENT') {
      setInvalidAccess(true)
      return
    }
  }, [userProductDetail])

  useEffect(() => {
    if (userProductDetail.isError && userProductDetail.error) {
      if (isAxiosError(userProductDetail.error)) {
        toast(userProductDetail.error.response?.data.message, userProductDetail.error)
      }
    }
  }, [userProductDetail.isError, userProductDetail.error])

  useEffect(() => {
    if (userProductSettle.isError && userProductSettle.error) {
      if (isAxiosError(userProductSettle.error)) {
        toast(userProductSettle.error.response?.data.message, userProductSettle.error)
      }
    }
  }, [userProductSettle.isError, userProductSettle.error])

  if (
    userProductDetail.isPending ||
    userProductDetail.isError ||
    userProductSettle.isPending ||
    userProductSettle.isError ||
    typeof userProductId !== 'number'
  ) {
    return (
      <Wrap>
        <Header type="SUB" title="정산하기" />
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  const goProductDetail = () => navigate(`/market/product/${userProductDetail.data.product.id}`)

  const toggleSaleAccordion = () => setAccordion((prevState) => ({ ...prevState, sale: !prevState.sale }))

  const toggleAmountAccordion = () => setAccordion((prevState) => ({ ...prevState, amount: !prevState.amount }))

  const toggleAgreed = (name: keyof typeof agreed) => {
    setAgreed((prevState) => ({ ...prevState, [name]: !prevState[name] }))
  }

  const handleRrn = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (!RRN_REGEX.test(value) || value.length > 14) return false

    // 뒷자리 2번째 부터는 마스킹 처리(화면에 보여지는 데이터)
    const masking = value
      .split('')
      .map((v, i) => (i > 7 ? '●' : v))
      .join('')

    setRrn((prevState) => {
      const prevValue = prevState.value

      // 입력값이 6글자일때 (앞자리를 모두 입력했을때)
      if (prevValue.length === 5 && value.length === 6) {
        return {
          value: `${value}-`,
          masking: `${value}-`,
        }
      }
      // 이전 입력값이 6글자일때(앞자리를 모두 입력한 상황일때) -(하이픈)이 없다면 붙여주고 입력값 적용
      if (prevValue.length === 6 && !prevValue.includes('-') && prevValue <= value) {
        const addedPart = value.slice(prevValue.length)
        return {
          value: `${prevValue}-${addedPart}`,
          masking: `${prevValue}-${addedPart}`,
        }
      }
      // ctrl 누르고 지우기 등 값이 아예 사라지면 초기화
      if (!value) {
        return { value: '', masking: '' }
      }
      // 입력값이 이전 입력값 보다 적다면(값을 지웠다면) 이전 값에서 현재 값 만큼 지움, 그게 아니면 뒤에 그대로 붙여줌
      // ctrl 누르고 backspace 누르는 현상 대비
      const addedPart = value.slice(prevValue.length)
      const removePart = prevValue.substring(0, value.length)
      return {
        value: value.length < prevValue.length ? removePart : `${prevValue}${addedPart}`,
        masking,
      }
    })
  }

  const isRrn = () => {
    // 유저 정보 없음
    if (!userInfo || !userInfo.identification) return false

    // 주민등록번호 미입력
    if (!rrn || !rrn.value || !rrn.value.length) return false

    // 주민등록번호 xxxxxx-xxxxxxx(하이픈 포함 14글자) 미달
    if (rrn.value.length !== 14) return false

    const birth = rrn.value.substring(0, 6) // 주민등록번호 앞자리 (생일)
    const gender = rrn.value.substring(7, 8) // 주민등록번호 뒷자리의 첫번째 (성별)
    const identificationBirth = dayjs(userInfo.identification.danalBirthDate).format('YYMMDD') // 본인인증 정보의 생년월일
    const identificationGender = userInfo.identification.danalGender as 'MALE' | 'FEMALE' | 'OTHER' // 본인인증 정보의 성별

    // 주민등록번호의 생년월일과 본인인증 생년월일이 다른 경우
    if (birth !== identificationBirth) return false

    const birthYear = dayjs(userInfo.identification.danalBirthDate).format('YYYY')
    const century = birthYear.substring(0, 2)

    // 남자
    if (identificationGender === 'MALE') {
      // 20세기 출생자인데 주민등록번호 뒷자리 첫글자가 1이 아닌 경우
      if (century === '19' && gender !== '1') return false
      // 21세기 출생자인데 주민등록번호 뒷자리 첫글자가 3이 아닌 경우
      if (century === '20' && gender !== '3') return false
    }

    // 여자
    if (identificationGender === 'FEMALE') {
      // 20세기 출생자인데 주민등록번호 뒷자리 첫글자가 2가 아닌 경우
      if (century === '19' && gender !== '2') return false
      // 21세기 출생자인데 주민등록번호 뒷자리 첫글자가 4가 아닌 경우
      if (century === '20' && gender !== '4') return false
    }

    return true
  }

  const goSettlement = () => navigate(`/myPage/asset/${userProductId}/settlement`, { replace: true })

  const calculate = () => {
    openModal({
      id: MODALS_ID.CALCULATE,
      Component: () => <CalculateConfirmModal userProductId={userProductId} rrn={rrn.value} success={goSettlement} />,
    })
  }

  const isInvalidRrn = rrn.value.length === 14 && !isRrn()
  const isFirstSellProducts = [25, 61].includes(userProductDetail.data.productId)

  return (
    <Wrap>
      <Header type="SUB" title="정산하기" />
      <StyledContent backgroundColor="gray_1">
        <ProductInfoWrap>
          {/* 작품 정보 */}
          <ProductInfoCard>
            <ProductInfoContent $gap={16}>
              <ProductImage>
                <img src={userProductDetail?.data?.product.imageUri} alt={userProductDetail?.data?.product.name} />
              </ProductImage>
              <ColumnItem $gap={4}>
                <Text size={18} weight={600}>
                  {userProductDetail?.data?.product.artist}
                </Text>
                <Text size={14} color="text_secondary">
                  {userProductDetail?.data?.product.name}
                </Text>
              </ColumnItem>
              <ProductDetailLink onClick={goProductDetail}>
                <Text size={14} weight={500} color="text_secondary">
                  작품 상세 보기
                </Text>
                <ArrowRightGrayIcon />
              </ProductDetailLink>
            </ProductInfoContent>
            <DivideLine />
            <ColumnItem>
              <SaleInfoToggleBox isActive={accordion.sale}>
                <Card $direction="column" $gap={16} $padding="16px" $radius={8} $background="gray_1">
                  <RowData
                    title="배정수량"
                    content={{
                      value: `${userProductDetail?.data?.totalShares.toLocaleString()}주`,
                      size: 18,
                      weight: 600,
                    }}
                  />
                  <RowData
                    title="세후청산금"
                    content={{
                      value: `${userProductSettle.data?.balance.toLocaleString()}원`,
                      size: 18,
                      weight: 600,
                    }}
                  />
                </Card>
                <ArrowDownRoundIcon onClick={toggleSaleAccordion} />
              </SaleInfoToggleBox>
              <Accordion isActive={accordion.sale}>
                <ColumnItem $gap={16}>
                  <RowData
                    title="배정공고일"
                    content={`${dayjs(userProductSettle.data?.allotmentDate).format('YYYY-MM-DD')}`}
                  />
                  <RowData
                    title="매각완료일"
                    content={`${dayjs(userProductSettle.data?.sellDate).format('YYYY-MM-DD')}`}
                  />
                  <RowData title="보유기간" content={`${userProductSettle.data?.holdingDays}일`} />
                  <RowData
                    title="증권수량"
                    content={`${userProductSettle.data?.totalIssuedShares.toLocaleString()}주`}
                  />
                  <RowData
                    title="모집총액"
                    content={`${userProductSettle.data?.totalIssuedPrice.toLocaleString()}원`}
                  />
                  <RowData
                    title={isFirstSellProducts ? '작품청산금' : '세전청산금'}
                    content={`${userProductSettle.data?.revenuePrice.toLocaleString()}원`}
                  />
                  {!isFirstSellProducts && (
                    <RowData
                      isChildren={true}
                      title="매각대금"
                      content={`${userProductSettle.data?.sellPrice.toLocaleString()}원`}
                    />
                  )}
                  <RowData
                    isChildren={true}
                    title="판매수수료(VAT포함)"
                    content={`${userProductSettle.data?.sellFee.toLocaleString()}원`}
                  />
                  <RowData
                    isChildren={true}
                    title="성과보수(VAT포함)"
                    content={`${userProductSettle.data?.carriedInterest.toLocaleString()}원`}
                  />
                  <RowData title="모집가액" content={`${userProductSettle.data?.offerPrice.toLocaleString()}원`} />
                  <RowData
                    title="주당 세전청산금"
                    content={`${userProductSettle.data?.sharePrice.toLocaleString()}원`}
                  />
                  <RowData
                    title="세전수익률"
                    content={{
                      value:
                        `${(userProductSettle.data?.revenueRate ?? 0) >= 0.0 ? '+' : '-'}` +
                        `${userProductSettle.data?.revenueRate ?? 0}%`,
                      color: (userProductSettle.data?.revenueRate ?? 0) >= 0.0 ? 'error' : 'primary_4',
                    }}
                  />
                </ColumnItem>
              </Accordion>
            </ColumnItem>
          </ProductInfoCard>
          {/* 세후청산금 산정 근거 */}
          <ProductInfoCard>
            <RowData title="배정금액" content={`${userProductSettle.data?.originalBalance.toLocaleString()}원`} />
            <RowData title="세전청산금" content={`${userProductSettle.data?.userRevenuePrice.toLocaleString()}원`} />
            <RowData title="배당소득세" content={`${userProductSettle.data?.otherIncomeTax.toLocaleString()}원`} />
            <RowData title="지방소득세" content={`${userProductSettle.data?.localTax.toLocaleString()}원`} />
            <DivideLine />
            <ColumnItem>
              <RowBetween>
                <Text size={18} weight={600}>
                  세후청산금
                </Text>
                <AmountInfoToggleBox isActive={accordion.amount}>
                  <Text size={18} weight={600} color="error">
                    {userProductSettle.data?.balance.toLocaleString()}원
                  </Text>
                </AmountInfoToggleBox>
              </RowBetween>
            </ColumnItem>
          </ProductInfoCard>
          <OtherIncomeTaxDescBox>
            <OtherIncomeTaxToggleBox $gap={2} onClick={toggleAmountAccordion} isActive={accordion.amount}>
              <Text size={14} weight={600} block={true}>
                📌 배당소득세 산정방식
              </Text>
              <ArrowDownIcon />
            </OtherIncomeTaxToggleBox>
            <Accordion isActive={accordion.amount}>
              <Text size={14} lineHeight={22} color="text_secondary">
                • 배정금액 = 모집가액 × 배정수량
                <br />• 세전청산금 = {isFirstSellProducts ? '작품청산금' : '(매각대금 – 판매수수료 – 성과보수)'} ÷
                증권수량 × 배정수량
                <br />
                • 배당소득 = 세전청산금 – 배정금액
                <br />
                • 배당소득세 = 배당소득 × 14%
                <br />
                • 지방소득세 = 배당소득세 × 10%
                <br />
                <br />
                ※ 배당 소득세 및 지방 소득세 모두 원단위는 절사합니다.
                <br />
                ※ 연간 금융소득(이자+배당) 2,000만 원 이하인 투자자는 원천징수로 납세가 종료됩니다.
                <br />※ 연간 금융소득(이자+배당)의 합이 연 2,000만원 초과시 종합과세가 적용됩니다. 이 경우 다른 소득과
                합산되어 더 높은 세율이 적용될 수 있으니, 다른 금융소득이 있으시다면 이 점을 꼭 확인해주세요.
              </Text>
            </Accordion>
          </OtherIncomeTaxDescBox>
          {/* 정산 동의 */}
          <ProductInfoCard>
            <AgreedCheckbox onClick={() => toggleAgreed('procedure')}>
              {agreed.procedure ? <CheckedIcon /> : <UncheckedIcon />}
              <Text size={14} lineHeight={22} color="text_secondary">
                (필수) 청산금을 지급받음으로써 미술작품 매각 관련 절차가 완료되었음을 확인했어요.
              </Text>
            </AgreedCheckbox>
            <AgreedCheckbox onClick={() => toggleAgreed('personalProvide')}>
              {agreed.personalProvide ? <CheckedIcon /> : <UncheckedIcon />}
              <Text size={14} lineHeight={22} color="text_secondary">
                (필수) 소득세 신고를 위한 개인정보제공에 동의해요.
              </Text>
            </AgreedCheckbox>
            <AgreedCheckbox onClick={() => toggleAgreed('duty')}>
              {agreed.duty ? <CheckedIcon /> : <UncheckedIcon />}
              <Text size={14} lineHeight={22} color="text_secondary">
                (필수) 주민등록번호 오기입으로 인해 발생한 불이익에 대해서 회사는 책임을 부담하지 않아요.
              </Text>
            </AgreedCheckbox>
          </ProductInfoCard>
          {/* 주민등록번호 입력 */}
          <ProductInfoCard>
            <Text size={18} weight={600}>
              주민등록번호 입력
            </Text>
            <ColumnItem $gap={8}>
              <Input
                inputMode="numeric"
                maxLength={14}
                value={rrn.masking}
                placeholder="주민등록번호를 입력해주세요."
                onChange={handleRrn}
                $borderColor={isInvalidRrn ? 'error' : undefined}
              />
              {isInvalidRrn ? (
                <Text size={12} color="error">
                  입력하신 정보와 본인인증 정보가 일치하지 않아요.
                </Text>
              ) : (
                <Text size={12} color="text_secondary">
                  소득세 신고를 위해 주민등록번호 기입이 필수입니다.
                </Text>
              )}
            </ColumnItem>
          </ProductInfoCard>
        </ProductInfoWrap>
        <NoticeContainer
          title="안내사항"
          list={[
            '정확한 주민등록번호를 기입해 주세요.',
            '입력하신 주민등록번호는 암호화되어 안전하게 보호되며, 소득세 신고 외에 어디에도 사용되지 않아요.',
            '입력하신 개인정보는 본인 동의 없이 제3자에게 제공되지 않아요.',
          ]}
          $backgroundColor="gray_1"
        />
      </StyledContent>
      <Footer>
        <Button
          type="primary"
          size="large"
          disabled={!Object.values(agreed).every((v) => v) || !isRrn()}
          block={true}
          onClick={calculate}
        >
          {!Object.values(agreed).every((v) => v)
            ? '약관 동의가 필요해요'
            : !isRrn()
              ? '주민등록번호가 필요해요'
              : '정산하기'}
        </Button>
      </Footer>
    </Wrap>
  )
}

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`

const ProductInfoWrap = styled(ColumnItem)`
  gap: 16px;
  padding: 16px;
  background-color: ${Color.gray_1};
`

const ProductInfoContent = styled(AlignCenterItem)`
  position: relative;
`

const ProductImage = styled.div`
  overflow: hidden;
  width: 100px;
  aspect-ratio: 1/1.3;
  border-radius: 8px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductDetailLink = styled(AlignCenterItem)`
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  outline: none;
  cursor: pointer;
`

const SaleInfoToggleBox = styled(ColumnItem)<{ isActive: boolean }>`
  gap: 8px;
  > svg {
    margin: 0 auto;
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
    cursor: pointer;
  }
`

const Accordion = styled(ColumnItem)<{ isActive: boolean }>`
  overflow: hidden;
  max-height: ${({ isActive }) => (isActive ? '100vh' : 0)};
  transition: 0.5s;
  & > * {
    margin-top: 16px;
  }
`

const AmountInfoToggleBox = styled(AlignCenterItem)<{ isActive: boolean }>`
  gap: 8px;
  > svg {
    margin-left: auto;
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
  }
`

const OtherIncomeTaxDescBox = styled(ColumnItem)`
  padding: 0 8px;
`

const OtherIncomeTaxToggleBox = styled(AlignCenterItem)<{ isActive: boolean }>`
  cursor: pointer;
  > svg {
    transform: rotate(${({ isActive }) => (isActive ? '-180deg' : '-0')});
    transition: 0.5s;
  }
`

const AgreedCheckbox = styled(AlignCenterItem)`
  gap: 8px;
  cursor: pointer;
  > svg {
    flex-shrink: 0;
  }
`
